import React from 'react';
import { useBehavior, useButton, useTriggerAction } from '@libs/hooks';
import { ActionActionTypeEnum, TWidgetProps } from '@libs/types';
import { CoreLayoutView, CoreTextView, ORIENTATION_ENUM } from '@core/widgets';
import { TInfoBannerData } from '../../types/data';
import { TInfoBannerOptions } from '../../types/options';
import { VisualView } from '../Image/VisualView';

export const InfoBanner = ({ widget }: TWidgetProps<TInfoBannerData, TInfoBannerOptions>) => {
    const { isHidden, isDisabled } = useBehavior(widget);
    const { triggerActionsByType, triggerActionsByNameAndType } = useTriggerAction(widget);
    const { handleClick } = useButton(widget, { isDisabled, triggerActionsByType, triggerActionsByNameAndType });
    const messages = widget.data?.messages ?? {};
    const options = widget.options ?? {};
    const parentStyleNames = [options.style ?? ' '].filter(Boolean) as string[];
    const hasOnClickAction = widget.actions?.some((a) => a?.actionType === ActionActionTypeEnum.onClick) ?? false;

    return (
        <CoreLayoutView
            uid={widget.uid}
            isHide={isHidden}
            isDisabled={isDisabled}
            parentStyleNames={parentStyleNames}
            styleName="infoBannerHorizontalLayout"
            onClick={hasOnClickAction ? () => handleClick() : undefined}
            styleProps={{
                orientation: ORIENTATION_ENUM.HORIZONTAL,
                lineY: 'center',
                padding: { top: 16, right: 16, bottom: 16, left: 16 },
                gap: 16,
                margin: options.margin,
                backgroundColor: 'var(--color-white)',
                borderRadius: 12,
                boxShadow: 'var(--color-shadow_grey)',
                overflow: 'hidden',
                pressed: {
                    backgroundColor: 'var(--color-grey_8_lc)',
                },
            }}
        >
            {options.imageName && (
                <VisualView
                    parentStyleNames={[...parentStyleNames, 'infoBannerIcon']}
                    iconName={options.imageName}
                    iconHierarchy="primary"
                    iconVariant="circle"
                    isDisabled={isDisabled}
                    opacity={isDisabled ? 0.4 : 1}
                />
            )}
            <CoreLayoutView
                parentStyleNames={parentStyleNames}
                styleName="infoBannerVerticalLayout"
                styleProps={{
                    orientation: ORIENTATION_ENUM.VERTICAL,
                    lineX: 'start',
                    width: '100%',
                    gap: 4,
                }}
            >
                {messages.title && (
                    <CoreTextView
                        parentStyleNames={parentStyleNames}
                        styleName="infoBannerTitle"
                        isDisabled={isDisabled}
                        text={messages.title}
                        styleProps={{
                            fontStyle: { fontSize: 17, lineHeight: 24 },
                            main: 'var(--text-primary, var(--color-black))',
                            maxLines: 2,
                            disabled: {
                                main: 'var(--color-grey_40_lc)',
                            },
                        }}
                    />
                )}
                {messages.subtitle && (
                    <CoreTextView
                        parentStyleNames={parentStyleNames}
                        styleName="infoBannerSubtitle"
                        isDisabled={isDisabled}
                        text={messages.subtitle}
                        styleProps={{
                            fontStyle: { fontSize: 14, lineHeight: 20 },
                            main: 'var(--text-secondary, var(--color-grey_72_lc))',
                            maxLines: 2,
                            disabled: {
                                main: 'var(--color-grey_40_lc)',
                            },
                        }}
                    />
                )}
            </CoreLayoutView>
        </CoreLayoutView>
    );
};
